import { lazy } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { AuthGuard } from '../auth/AuthGuard';
import { AppShell } from './components/AppShell';

function GuestOnlyRoute({ element }: { element: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <>{element}</>;
}

function RedirectToWi() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/wi/${id}`} replace />;
}

const AuthCallbackView = lazy(() => import('./views/AuthCallbackView'));
const AuthErrorView = lazy(() => import('./views/AuthErrorView'));

// ── Code Factory feature views ────────────────────────────────────────────────
const FactoryPipelineListView = lazy(() => import('./views/factory/PipelineListView'));
const FactoryPipelineDetailView = lazy(() => import('./views/factory/PipelineDetailView'));
const FactoryPipelineEditView = lazy(() => import('./views/factory/PipelineEditView'));
const FactoryRunListView = lazy(() => import('./views/factory/RunListView'));
const FactoryRunDetailView = lazy(() => import('./views/factory/RunDetailView'));
const FactoryApprovalQueueView = lazy(() => import('./views/factory/ApprovalQueueView'));
const FactoryTriggersView = lazy(() => import('./views/factory/TriggersView'));
const FactoryDiagnosticsView = lazy(() => import('./views/factory/DiagnosticsView'));
const LoginView = lazy(() => import('./views/LoginView'));
const DashboardView = lazy(() => import('./views/DashboardView'));
const WorkflowsView = lazy(() =>
  import('@avila-roffe/features-araf').then((m) => ({ default: m.WorkflowsPage })),
);
const WorkflowDetailView = lazy(() => import('./views/QueueDetailView'));
const AgentsView = lazy(() =>
  import('@avila-roffe/features-araf').then((m) => ({ default: m.AgentsPage })),
);
const AgentDetailView = lazy(() =>
  import('@avila-roffe/features-araf').then((m) => ({ default: m.AgentDetailPage })),
);
const SkillsView = lazy(() =>
  import('@avila-roffe/features-araf').then((m) => ({ default: m.SkillsPage })),
);
const SkillDetailView = lazy(() =>
  import('@avila-roffe/features-araf').then((m) => ({ default: m.SkillDetailPage })),
);
const ToolsView = lazy(() =>
  import('@avila-roffe/features-araf').then((m) => ({ default: m.ToolsPage })),
);
const ToolDetailView = lazy(() =>
  import('@avila-roffe/features-araf').then((m) => ({ default: m.ToolDetailPage })),
);
const IntakeView = lazy(() => import('./views/IntakeView'));
const ReviewView = lazy(() => import('./views/ReviewView'));
const ChatView = lazy(() => import('./views/ChatView'));
const PlaygroundView = lazy(() => import('./views/PlaygroundView'));
const ProfileView = lazy(() => import('./views/ProfileView'));
const WorkersDashboardView = lazy(() => import('./views/WorkersDashboardView'));
const SettingsView = lazy(() => import('./views/SettingsView'));
const PreferencesView = lazy(() => import('./views/PreferencesView'));
const StatusView = lazy(() => import('./views/StatusView'));
const ExecutorQueuesDashboard = lazy(() => import('./views/ExecutorQueuesDashboard'));
const ExecutorQueueDetailView = lazy(() => import('./views/ExecutorQueueDetailView'));
const PipelineView = lazy(() => import('./views/PipelineView'));
const WorkItemsView = lazy(() => import('./views/WorkItemsView'));
const DocumentationView = lazy(() => import('./views/DocumentationView'));
const TrackerWorkItemDetailView = lazy(() => import('./views/TrackerWorkItemDetailView'));

export function AppRouter() {
  return (
    <Routes>
      {/* Auth routes — public, no shell, no guard */}
      <Route path="/login" element={<GuestOnlyRoute element={<LoginView />} />} />
      <Route path="/auth/callback" element={<AuthCallbackView />} />
      <Route path="/auth/error" element={<AuthErrorView />} />

      {/* Shell-wrapped protected routes */}
      <Route
        path="/"
        element={
          <AuthGuard>
            <AppShell>
              <Navigate to="/dashboard" replace />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/dashboard"
        element={
          <AuthGuard>
            <AppShell>
              <DashboardView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/queues"
        element={
          <AuthGuard>
            <AppShell>
              <ExecutorQueuesDashboard />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/queues/:slug"
        element={
          <AuthGuard>
            <AppShell>
              <ExecutorQueueDetailView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/workflows"
        element={
          <AuthGuard>
            <AppShell>
              <WorkflowsView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/workflows/:id"
        element={
          <AuthGuard>
            <AppShell>
              <WorkflowDetailView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/agents"
        element={
          <AuthGuard>
            <AppShell>
              <AgentsView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/agents/:id"
        element={
          <AuthGuard>
            <AppShell>
              <AgentDetailView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/skills"
        element={
          <AuthGuard>
            <AppShell>
              <SkillsView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/skills/:id"
        element={
          <AuthGuard>
            <AppShell>
              <SkillDetailView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/tools"
        element={
          <AuthGuard>
            <AppShell>
              <ToolsView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/tools/:id"
        element={
          <AuthGuard>
            <AppShell>
              <ToolDetailView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/wi/:id"
        element={
          <AuthGuard>
            <AppShell>
              <ReviewView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/work-items"
        element={
          <AuthGuard>
            <AppShell>
              <WorkItemsView />
            </AppShell>
          </AuthGuard>
        }
      />
      {/* Tracker work item detail */}
      <Route
        path="/work-items/:id"
        element={
          <AuthGuard>
            <AppShell>
              <TrackerWorkItemDetailView />
            </AppShell>
          </AuthGuard>
        }
      />
      {/* Legacy redirects */}
      <Route path="/review/:id" element={<RedirectToWi />} />
      <Route
        path="/intake"
        element={
          <AuthGuard>
            <AppShell>
              <IntakeView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/review/:id"
        element={
          <AuthGuard>
            <AppShell>
              <ReviewView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/chat"
        element={
          <AuthGuard>
            <AppShell>
              <ChatView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/playground"
        element={
          <AuthGuard>
            <AppShell>
              <PlaygroundView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/profile"
        element={
          <AuthGuard>
            <AppShell>
              <ProfileView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/workers"
        element={
          <AuthGuard>
            <AppShell>
              <WorkersDashboardView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/pipeline"
        element={
          <AuthGuard>
            <AppShell>
              <PipelineView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/settings"
        element={
          <AuthGuard>
            <AppShell>
              <SettingsView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/settings/:section"
        element={
          <AuthGuard>
            <AppShell>
              <SettingsView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/settings/:section/:subSection"
        element={
          <AuthGuard>
            <AppShell>
              <SettingsView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/preferences"
        element={
          <AuthGuard>
            <AppShell>
              <PreferencesView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/preferences/:section"
        element={
          <AuthGuard>
            <AppShell>
              <PreferencesView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/status"
        element={
          <AuthGuard>
            <AppShell>
              <StatusView />
            </AppShell>
          </AuthGuard>
        }
      />

      <Route
        path="/documentation"
        element={
          <AuthGuard>
            <AppShell>
              <DocumentationView />
            </AppShell>
          </AuthGuard>
        }
      />

      {/* ── Code Factory routes ──────────────────────────────────── */}
      <Route
        path="/factory/approvals"
        element={
          <AuthGuard>
            <AppShell>
              <FactoryApprovalQueueView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/factory/diagnostics"
        element={
          <AuthGuard>
            <AppShell>
              <FactoryDiagnosticsView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/factory/triggers"
        element={
          <AuthGuard>
            <AppShell>
              <FactoryTriggersView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/factory/pipelines"
        element={
          <AuthGuard>
            <AppShell>
              <FactoryPipelineListView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/factory/pipelines/new"
        element={
          <AuthGuard>
            <AppShell>
              <FactoryPipelineEditView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/factory/pipelines/:id"
        element={
          <AuthGuard>
            <AppShell>
              <FactoryPipelineDetailView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/factory/pipelines/:id/edit"
        element={
          <AuthGuard>
            <AppShell>
              <FactoryPipelineEditView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/factory/runs"
        element={
          <AuthGuard>
            <AppShell>
              <FactoryRunListView />
            </AppShell>
          </AuthGuard>
        }
      />
      <Route
        path="/factory/runs/:id"
        element={
          <AuthGuard>
            <AppShell>
              <FactoryRunDetailView />
            </AppShell>
          </AuthGuard>
        }
      />
    </Routes>
  );
}

export default AppRouter;
